/**
 * Guided tours of the main flows (FR-TRV-30), on the parcours of docs/parcours-utilisateurs.md.
 * A step points at an element marked with data-tour="<anchor>"; when the page does not show it
 * (a role without the button, a phone without the side menu), the step is shown in the middle of
 * the screen instead. Texts: tours.<tour>.<step>.title / .text in the locale files.
 */
export type TourId = 'discover' | 'expeditions' | 'pickups' | 'registre' | 'wastes';

export interface TourStep {
  key: string;
  /** data-tour anchor */
  anchor: string | null;
  /** beside a tall element (the side menu): the end side, mirrored in Arabic */
  beside?: boolean;
}

export interface Tour {
  id: TourId;
  /** the page it runs on */
  path: string;
  steps: TourStep[];
}

const step = (key: string, anchor: string | null = key, beside = false): TourStep => ({
  key,
  anchor,
  beside,
});

export const TOURS: Tour[] = [
  {
    id: 'discover',
    path: '/',
    steps: [
      step('site'),
      step('nav', 'nav', true),
      step('tasks'),
      step('notifications'),
      step('help'),
      step('account'),
    ],
  },
  {
    id: 'expeditions',
    path: '/expeditions',
    steps: [step('create', 'page-actions'), step('views'), step('filters'), step('list')],
  },
  {
    id: 'pickups',
    path: '/pickup-requests',
    steps: [step('create', 'page-actions'), step('filters'), step('list')],
  },
  {
    id: 'registre',
    path: '/registre',
    steps: [step('filters'), step('totals'), step('export', 'page-actions'), step('list')],
  },
  {
    id: 'wastes',
    path: '/wastes',
    steps: [step('create', 'page-actions'), step('filters'), step('list')],
  },
];

export function tourFor(pathname: string): Tour | undefined {
  return TOURS.find((t) => t.id !== 'discover' && t.path === pathname);
}

/** The element a step points at, when it is on the page and visible. */
export function anchorElement(anchor: string | null): HTMLElement | null {
  if (!anchor) return null;
  const element = document.querySelector<HTMLElement>(`[data-tour="${anchor}"]`);
  if (!element) return null;
  const box = element.getBoundingClientRect();
  return box.width > 0 && box.height > 0 ? element : null;
}

/**
 * The help topic of a page (help.topics.<topic> in the locale files), by its path: the main
 * flows each have theirs, every other page the general one.
 */
const TOPICS: [RegExp, string][] = [
  [/^\/$/, 'dashboard'],
  [/^\/expeditions/, 'expeditions'],
  [/^\/chargements/, 'chargement'],
  [/^\/pickup-requests/, 'pickups'],
  [/^\/returns/, 'returns'],
  [/^\/transfers/, 'transfers'],
  [/^\/stock/, 'stock'],
  [/^\/registre/, 'registre'],
  [/^\/annual-report/, 'annualReport'],
  [/^\/compliance/, 'compliance'],
  [/^\/wastes/, 'wastes'],
  [/^\/caps/, 'caps'],
  [/^\/filieres/, 'routes'],
  [/^\/prestataires/, 'prestataires'],
  [/^\/(contracts|invoices)/, 'costs'],
  [/^\/imports/, 'imports'],
  [/^\/admin\/users/, 'users'],
];

export function helpTopicFor(pathname: string): string {
  return TOPICS.find(([pattern]) => pattern.test(pathname))?.[1] ?? 'general';
}

export const HELP_TOPICS = [...new Set(TOPICS.map(([, topic]) => topic)), 'general'];
